import { getErrorMessage } from '~libs/axiosWrapper';
import { buildSlice, CommonState, LoadingState } from '~libs/reduxUtils';
import { Appointment, Worker } from '~weekly-planner/lib/common';
import { SimulateParams } from '~weekly-planner/lib/simulate';

import { typePrefix, usersApi } from '~weekly-planner/actions/users';

interface LoadingRows {
  [id: number]: LoadingState;
}

interface State extends CommonState<Worker> {
  loadingRows: LoadingRows;
  params: { [key: string]: any };
  simulated: Worker[];
  filterOptions: { [key: string]: any };
  orderBy: {
    availability: false;
    distance: false;
  };
}

const initialState: State = {
  loading: 'idle',
  loadingRows: {},
  hasLoaded: false,
  error: null,
  params: {},
  rows: [],
  simulated: [],
  filterOptions: {
    is_qualified: null,
    is_available: null,
    is_preferred: null,
    is_historical: null,
  },
  orderBy: {
    availability: false,
    distance: false,
  },
};

const slice = buildSlice<State, Worker>(typePrefix, initialState, {
  rtkQueryBridges: [
    {
      endpoint: usersApi.endpoints.getUsers,
      mode: 'list',
      selectRows: (payload: Worker[]) => payload,
    },
  ],
  customActions: {
    clearSimulated(state) {
      state.simulated = [];
      state.loadingRows = {};
    },
    filterUsers(state, action) {
      const { payload } = action;
      if ('key' in payload && 'value' in payload) {
        state.filterOptions[payload.key] = payload.value;
      } else {
        Object.entries(payload).forEach(([key, value]) => {
          state.filterOptions[key] = value;
        });
      }
    },
    orderUsers(state, action) {
      const { payload } = action;
      state.orderBy = payload;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // simulate.pending: set per-worker loadingRows based on type of simulation
    builder.addMatcher(usersApi.endpoints.simulate.matchPending, (state, action) => {
      const arg = (action.meta.arg as unknown as { originalArgs: SimulateParams }).originalArgs;
      const { type } = arg;
      switch (type) {
        case 'refresh': {
          const refreshArg = arg as { type: 'refresh'; workers: Worker[] };
          refreshArg.workers.forEach((worker: Worker) => {
            state.loadingRows[parseInt(worker.id)] = 'pending';
          });
          break;
        }
        case 'assign': {
          const assignArg = arg as { type: 'assign'; data: Appointment[] };
          assignArg.data.forEach((appointment: Appointment) => {
            let { user_id } = appointment;
            if (user_id) {
              if (typeof user_id === 'string') user_id = parseInt(user_id);
              state.loadingRows[user_id] = 'pending';
            }
          });
          break;
        }
        case 'move':
        default: {
          const moveArg = arg as { type: 'move'; workers: { origin?: Worker; target: Worker } };
          const { origin, target } = moveArg.workers;
          if (origin) state.loadingRows[parseInt(origin.id)] = 'pending';
          if (target) state.loadingRows[parseInt(target.id)] = 'pending';
          break;
        }
      }
    });
    // simulate.fulfilled: upsert simulated workers
    builder.addMatcher(usersApi.endpoints.simulate.matchFulfilled, (state, action) => {
      const updatedWorkers = action.payload.data;
      if (!updatedWorkers) return;
      updatedWorkers.forEach((worker: any) => {
        state.loadingRows[worker.id] = 'fulfilled';
        const index = state.simulated.findIndex((w: { id: any }) => w.id === worker.id);
        if (index > -1) {
          state.simulated[index] = worker;
        } else {
          state.simulated.push(worker);
        }
      });
      state.loading = 'fulfilled';
    });
    // simulate.rejected: mark loading as declined
    builder.addMatcher(usersApi.endpoints.simulate.matchRejected, (state) => {
      state.loading = 'declined';
      // Clear the per-worker spinners set in matchPending. A failed simulate
      // completed none of them, so leaving any as 'pending' freezes those
      // worker cards with no user-visible recovery short of a page reload.
      state.loadingRows = {};
    });
    // sendSMS: preserve whole-grid main spinner behaviour
    builder.addMatcher(usersApi.endpoints.sendSMS.matchPending, (state) => {
      state.loading = 'pending';
    });
    builder.addMatcher(usersApi.endpoints.sendSMS.matchFulfilled, (state) => {
      state.loading = 'fulfilled';
      state.error = null;
    });
    builder.addMatcher(usersApi.endpoints.sendSMS.matchRejected, (state, action) => {
      state.loading = 'declined';
      // payload carries the transformed API error (ApiError); error carries the
      // SerializedError fallback. getErrorMessage handles both shapes, so we
      // avoid an untyped `any` cast and reuse the canonical extractor.
      state.error =
        typeof action.payload === 'string' ? action.payload : getErrorMessage(action.payload ?? action.error);
    });
  },
});

export const { clear, clearSimulated, updateParams, filterUsers, orderUsers, clearError } = slice.actions;
export default slice.reducer;
